import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Tooltip,
  Badge,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Checkbox,
  ListItemText
} from "@mui/material";
import StyledDataGrid from "../../../components/StyledDataGrid";
import { fetchAPI, fetchUniqueAliquotBarcodes } from "../../../utils/api";
import { useNavigate } from "react-router-dom";
import { BIOBANK_SUB_ROUTES } from "../../../constants/routes";

const CheckInTab = () => {
  const navigate = useNavigate();
  const [summaryData, setSummaryData] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  
  const [uniqueBarcodes, setUniqueBarcodes] = useState([]);
  const [barcodesLoading, setBarcodesLoading] = useState(false);
  const [activeBarcodes, setActiveBarcodes] = useState([]);
  const [pendingBarcodes, setPendingBarcodes] = useState([]);
  const [selectOpen, setSelectOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadBarcodes = async () => {
      setBarcodesLoading(true);
      try {
         const res = await fetchUniqueAliquotBarcodes('Not Checked-In');
         let parsedBarcodes = [];
         if (Array.isArray(res)) parsedBarcodes = res;
         else if (res && Array.isArray(res.content)) parsedBarcodes = res.content;
         else if (res && Array.isArray(res.data)) parsedBarcodes = res.data;
         if (!cancelled) setUniqueBarcodes(parsedBarcodes);
      } catch (err) {
        console.error("Failed to fetch unique barcodes:", err);
      } finally {
        if (!cancelled) setBarcodesLoading(false);
      }
    };
    loadBarcodes();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const fetchSummary = async () => {
      setSummaryLoading(true);
      try {
        const params = new URLSearchParams();
        params.append("status", "Not Checked-In");
        if (activeBarcodes.length > 0) {
          params.append("barcodes", activeBarcodes.join(","));
        }
        const res = await fetchAPI(`biobank/aliquots/summary?${params.toString()}`);
        setSummaryData(res || []);
      } catch (err) {
        console.error("Failed to load summary:", err);
      } finally {
        setSummaryLoading(false);
      }
    };

    fetchSummary();
  }, [activeBarcodes]);

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>

      <Box sx={{ mb: 2, mt: 1, display: "flex", gap: 2, alignItems: "center" }}>
        <Badge badgeContent={activeBarcodes.length} sx={{ '& .MuiBadge-badge': { backgroundColor: '#500000', color: '#fff' } }}>
          <FormControl size="small" sx={{ width: 250 }} data-tour-id="tour-aliquot-checkin-search">
            <Select
              sx={{ 
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#500000' },
                ...(activeBarcodes.length > 0 && { backgroundColor: '#500000', color: '#fff', '& .MuiSvgIcon-root': { color: '#fff' } })
              }}
              multiple
              displayEmpty
              open={selectOpen}
              onOpen={() => { setPendingBarcodes(activeBarcodes); setSelectOpen(true); }}
              onClose={() => {
                setSelectOpen(false);
                if (JSON.stringify(pendingBarcodes) !== JSON.stringify(activeBarcodes)) {
                  setActiveBarcodes(pendingBarcodes);
                }
              }}
              value={selectOpen ? pendingBarcodes : activeBarcodes}
              onChange={(event) => {
                const { value } = event.target;
                if (value.includes('__clear__')) setPendingBarcodes([]);
                else setPendingBarcodes(typeof value === 'string' ? value.split(',') : value);
              }}
              renderValue={() => "Select Barcode"}
              MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
            >
              {barcodesLoading ? (
                <MenuItem disabled><CircularProgress size={20} sx={{ mr: 2 }} />Loading...</MenuItem>
              ) : (
                [
                <MenuItem key="__clear__" value="__clear__" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>Clear Selection</MenuItem>,
                ...uniqueBarcodes.map((barcode) => {
                  const isChecked = (selectOpen ? pendingBarcodes : activeBarcodes).indexOf(barcode) > -1;
                  return (
                    <MenuItem key={barcode} value={barcode}>
                      <Checkbox checked={isChecked} size="small" sx={{ color: '#500000', '&.Mui-checked': { color: '#500000' } }} />
                      <ListItemText primary={barcode} primaryTypographyProps={{ fontFamily: 'monospace' }} />
                    </MenuItem>
                  );
                })
              ])}
            </Select>
          </FormControl>
        </Badge>
      </Box>
      <StyledDataGrid
        rows={summaryData.map((item, idx) => ({ id: item.barcode || idx, ...item }))}
        columns={[
          { field: "barcode", headerName: "Barcode", flex: 1 },
          { field: "totalCount", headerName: "Total Aliquots", flex: 1 },
          { 
            field: "sampleTypes", 
            headerName: "Sample Breakdown", 
            flex: 2,
            renderCell: (params) => {
              const sampleArr = params.row.sample || [];
              const sampleMap = {};
              sampleArr.forEach(s => { sampleMap[s.type] = s.count; });
              const allTypes = ["A","B","C","D","E","F","G","H","I"];
              
              return (
                <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", height: "100%" }}>
                  {allTypes.map(type => {
                    const count = sampleMap[type] || 0;
                    const isCollected = count > 0;
                    return (
                      <Tooltip key={type} title={`count: ${count}`} arrow>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            backgroundColor: isCollected ? "#4caf50" : "#e0e0e0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: isCollected ? "#fff" : "#9e9e9e",
                            fontSize: "0.75rem",
                            fontWeight: "bold",
                            cursor: "pointer",
                            boxShadow: isCollected ? "0 1px 3px rgba(0,0,0,0.2)" : "none",
                          }}
                        >
                          {type}
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              );
            }
          },
          { 
            field: "action", 
            headerName: "Action", 
            flex: 1,
            renderCell: (params) => (
              <Button 
                variant="contained" 
                onClick={() => navigate(BIOBANK_SUB_ROUTES.ALIQUOT_MANAGEMENT_CHECK_IN_ALIQUOTS, {
                  state: {
                    barcode: params.row.barcode,
                    returnTo: BIOBANK_SUB_ROUTES.ALIQUOT_MANAGEMENT_CHECK_IN,
                  },
                })}
                sx={{ width: "140px", height: "32px", fontSize: "13px", fontWeight: 600, borderRadius: "10px" }}
              >
                Begin Check-In
              </Button>
            )
          }
        ]}
        loading={summaryLoading}
        hideFooter={true}
        pagination={false}
      />
    </Box>
  );
};

export default CheckInTab;
